import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';

import { Price } from '@app/entities/price';
import { PassengerArrangement, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { TagMemberType } from '@design-system/components/molecules/tag-member/entities/tag-member.types';
import { 
  PriceDetailCardDiscount, PriceDetailCardPrice, PriceDetailCardSubtitle
} from '@design-system/components/organisms/price-detail-card/entities/price-detail-card.types';
import {
  getPaymentDiscountAmount, getPaymentPercentageCalculated, getStrikethroughDiscount
} from '@shared/payment-detail/mappers/discount-calculated.mapper';
import { PriceDetailService } from '@shared/payment-detail/services/payment-detail.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-payment-detail',
  templateUrl: 'payment-detail.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PaymentDetailComponent {

  @Input() price: Price;
  @Input() currencyCode: string;
  @Input() conversionRate: number;
  @Input() passengerArrangement: PassengerArrangement[];
  @Input() isRappiProBrandig = false;

  @Input() productReference: number;

  @Input() showSwipeUp = false;
  @Input() isTooltip = false;
  @Input() isModal = false;
  @Input() isMobile = false;
  @Input() isBuses = false;
  @Input() isActivities = false;
  @Input() isUserHistory = false;

  @Output() toggleShowSwipeUp: EventEmitter<Event> = new EventEmitter<Event>();

  public readonly tagMemberType = TagMemberType;

  constructor(
    private readonly service: PriceDetailService,
  ) { }

  get discount$(): Observable<PriceDetailCardDiscount> {
    return this.service.getCardDiscount$(
      this.isActivities ? SearchBy.ACTIVITIES : SearchBy.FLIGHTS, this.discountAmount,
      this.discountPercentageValueProp, this.currencyCodeValid
    );
  }

  get rows(): PriceDetailCardPrice[][] {
    return [
      this.service.getCardGenericPaymentDetailSection(
        this.price?.Amount, this.price?.Taxes, this.price?.Fees, this.currencyCodeValid
      )
    ];
  }

  get subtitles(): PriceDetailCardSubtitle[] {
    return !this.isActivities ? 
      this.service.getCardSubtitlesByPassengers(this.mapPassengerArrengement) : [];
  }

  get discountAmount(): number {
    return getPaymentDiscountAmount(
      this.price?.Strikethrough, this.price?.Total, this.price?.DiscountAmount, this.strikethroughDiscount
    );
  }

  get discountPercentageValueProp(): number {
    return getPaymentPercentageCalculated(
      false, this.price?.Strikethrough, this.price?.Total, this.price?.DiscountAmount, this.strikethroughDiscount,
      this.price?.Amount, this.price?.Taxes,
    );
  }

  get mapPassengerArrengement(): PassengerArrangement[] {
    return this.isBuses ? this.passengerArrangementOnlySeats: this.passengerArrangement ? this.passengerArrangement.filter(
      data => data.quantity > 0
    ) : [];
  }

  get passengerArrangementOnlySeats(): PassengerArrangement[] {
    return this.passengerArrangement.filter(arrangement => arrangement.id === PassengerType.ADULTS);
  }

  get currencyCodeValid(): string {
    return this.price?.CurrencyCode || this.currencyCode;
  }

  private get strikethroughDiscount(): number {
    return getStrikethroughDiscount(this.price?.TotalDiscountInStrikethrough, this.price?.TotalDiscount);
  }
}
